import { memo, useMemo, useCallback, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Typography,
  Skeleton,
  IconButton,
  CircularProgress,
  Dialog,
  DialogContent,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import axios from 'src/utilities/http-client'
import { downloadFile } from 'src/v3/utilities/filesystem'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import {
  Warning as WarningIcon,
  Badge as LicenseIcon,
  GetApp as DownloadIcon,
  Edit as EditIcon,
  Verified as VerifyIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Add as AddIcon,
} from '@mui/icons-material'
import { VerificationStatus } from 'src/v3/core/types'
import type { RosterCredentialItem } from '../types'
import EditCredentialModal from './modals/edit-credential-modal'
import CreateCredentialModal from './modals/create-credential-modal'
import VerifyCredentialModal from './modals/verify-credential-modal'

dayjs.extend(utc)

interface CredentialsDataGridProps {
  credentialsRows: RosterCredentialItem[]
  selectedUserLicensesCredentialsStatus: string
  totalCredentials: number
  expiredCredentials: number
  expiringSoonCredentials: number
  userID: string
  verificationEnabled: boolean
  verificationStatuses?: Record<string, VerificationStatus>
  onRefresh: () => void
}

export const CredentialsDataGrid = memo(
  ({
    credentialsRows,
    selectedUserLicensesCredentialsStatus,
    totalCredentials,
    expiredCredentials,
    expiringSoonCredentials,
    userID,
    verificationEnabled,
    verificationStatuses,
    onRefresh,
  }: CredentialsDataGridProps) => {
    // Track downloading credential IDs (Set allows multiple simultaneous downloads)
    let [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set())

    // Track edit modal state
    let [editModalCredential, setEditModalCredential] =
      useState<RosterCredentialItem | null>(null)

    // Track create modal state
    let [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

    // Track verify form modal state
    let [verifyModalCredential, setVerifyModalCredential] =
      useState<RosterCredentialItem | null>(null)

    // Track view state
    let [viewBlob, setViewBlob] = useState<Blob | null>(null)
    let [viewingId, setViewingId] = useState<string>('')
    let [viewedCredential, setViewedCredential] =
      useState<RosterCredentialItem | null>(null)

    // Track snackbar state
    let [snackbar, setSnackbar] = useState<{
      isOpen: boolean
      message: string
      severity: 'success' | 'error'
    }>({
      isOpen: false,
      message: '',
      severity: 'success',
    })

    let rowsWithVerification = !verificationEnabled
      ? credentialsRows
      : credentialsRows.map((r) => ({
          ...r,
          verificationStatus: verificationStatuses?.[r.id] || 'pending',
        }))

    // Extension mapping for blob types
    let extensions: Record<string, string> = {
      'application/pdf': 'pdf',
      'image/jpeg': 'jpg',
      'image/png': 'png',
    }

    // Handle operation success with custom message
    let handleSuccess = useCallback(
      (message: string) => {
        setSnackbar({
          isOpen: true,
          message,
          severity: 'success',
        })
        onRefresh()
      },
      [onRefresh],
    )

    // Handle operation failure with error message
    let handleFailure = useCallback((error: string) => {
      setSnackbar({
        isOpen: true,
        message: error,
        severity: 'error',
      })
    }, [])

    // Handle credential download
    let handleDownload = useCallback(
      async (row: RosterCredentialItem) => {
        if (!row.fileUrl) return

        setDownloadingIds((prev) => new Set(prev).add(row.id))

        try {
          let fileResponse = await axios.get<Blob>(row.fileUrl, {
            responseType: 'blob',
          })
          let blob = fileResponse.data
          let extension = extensions[blob.type] || 'pdf'
          let filename = `${row.category.replace(/\s+/g, '_')}_${row.type.replace(/\s+/g, '_')}_${row.number.replace(/\s+/g, '_')}.${extension}`

          await downloadFile(blob, filename)
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Error downloading credential:', err)
        } finally {
          setDownloadingIds((prev) => {
            let next = new Set(prev)
            next.delete(row.id)
            return next
          })
        }
      },
      [extensions],
    )

    // Handle credential view
    let handleView = useCallback(async (row: RosterCredentialItem) => {
      if (!row.fileUrl || row.category !== 'Credential') return

      setViewingId(row.id)

      try {
        // Fetch the blob directly from fileUrl
        let fileResponse = await axios.get<Blob>(row.fileUrl, {
          responseType: 'blob',
        })

        setViewBlob(fileResponse.data)
        setViewedCredential(row)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error viewing credential:', err)
      } finally {
        setViewingId('')
      }
    }, [])

    // Memoized date formatters to avoid repeated dayjs parsing on every render
    let formatIssueDate = useCallback((value: Date | undefined) => {
      if (!value) return 'N/A'
      return dayjs(value).utc().format('MMM D, YYYY')
    }, [])

    let formatExpirationDate = useCallback((value: Date | undefined) => {
      if (!value) return 'N/A'
      return dayjs(value).utc().format('MMM D, YYYY')
    }, [])

    let formatDateAdded = useCallback(
      (value: Date | undefined) =>
        value ? dayjs(value).format('MMM D, YYYY') : '',
      [],
    )

    // Stable reference to check if credential is downloading
    let isCredentialDownloading = useCallback(
      (id: string) => downloadingIds.has(id),
      [downloadingIds],
    )

    // Credentials DataGrid columns (excluding userName and email for user details)
    let credentialsColumns = useMemo<GridColDef[]>(
      () => [
        {
          field: 'type',
          headerName: 'Type',
          width: 90,
        },
        {
          field: 'number',
          headerName: 'Number',
          width: 150,
        },
        {
          field: 'state',
          headerName: 'State',
          minWidth: 150,
          renderCell: (params) =>
            params.value || (
              <Box className="flex items-center justify-center h-full">
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              </Box>
            ),
        },
        {
          field: 'actions',
          headerName: 'Actions',
          flex: 0,
          minWidth: 150,
          sortable: false,
          filterable: false,
          disableExport: true,
          align: 'left',
          headerAlign: 'left',
          renderCell: (params) => {
            let row = params.row as RosterCredentialItem
            let isDownloading = isCredentialDownloading(row.id)

            let verifyDisabled = row.verificationStatus !== 'pending'
            let verifyTooltip = verifyDisabled
              ? row.verificationStatus === 'accepted'
                ? 'Credential has been accepted'
                : row.verificationStatus === 'rejected'
                  ? 'Credential has been rejected'
                  : 'Update Verification Status'
              : 'Update Verification Status'
            let showView = true
            let hasFile = !!row.fileUrl
            let isPdf = row.fileUrl?.includes('pdf')
            let isViewing = viewingId === row.id
            let viewDisabled = !hasFile || isPdf || isViewing
            let viewTooltip = !hasFile
              ? 'File not found'
              : isPdf
                ? 'Cannot preview PDF files. Download the file to view.'
                : 'View'

            return (
              <Box className="flex items-center justify-start h-full gap-1">
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    onClick={() => setEditModalCredential(row)}
                    aria-label="Edit credential"
                    sx={{ color: 'primary.main' }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {showView && (
                  <Tooltip title={viewTooltip}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleView(row)}
                        disabled={viewDisabled}
                        aria-label={viewTooltip}
                        sx={{ color: 'primary.main' }}
                      >
                        {isViewing ? (
                          <CircularProgress size={20} />
                        ) : (
                          <ViewIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                <Tooltip
                  title={
                    !hasFile
                      ? 'File not found'
                      : isDownloading
                        ? 'Downloading'
                        : 'Download'
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleDownload(row)}
                      disabled={!hasFile || isDownloading}
                      aria-label={
                        isDownloading
                          ? 'Downloading credential'
                          : 'Download credential'
                      }
                      sx={{ color: 'primary.main' }}
                    >
                      {isDownloading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <DownloadIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
                {verificationEnabled && (
                  <Tooltip title={verifyTooltip}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => setVerifyModalCredential(row)}
                        disabled={verifyDisabled}
                        aria-label={verifyTooltip}
                        sx={{ color: 'primary.main' }}
                      >
                        <VerifyIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
            )
          },
        },
        {
          field: 'verificationStatus',
          headerName: 'Verification',
          width: 120,
          sortable: false,
          align: 'left',
          headerAlign: 'left',
          renderCell: (params) => {
            let row = params.row as RosterCredentialItem

            let statusColors = {
              accepted: 'success.main',
              rejected: 'error.main',
              pending: 'warning.main',
            }

            let statusLabels = {
              accepted: 'Accepted',
              rejected: 'Rejected',
              pending: 'Pending',
            }

            if (!verificationEnabled) {
              return (
                <Box className="flex items-center justify-center h-full">
                  <Typography variant="body2" color="text.secondary">
                    -
                  </Typography>
                </Box>
              )
            }

            return (
              <Box className="flex items-center justify-start h-full">
                <Typography
                  variant="body2"
                  sx={{
                    color: row.verificationStatus
                      ? statusColors[row.verificationStatus]
                      : 'text.secondary',
                    fontWeight: 500,
                  }}
                >
                  {row.verificationStatus
                    ? statusLabels[row.verificationStatus]
                    : '-'}
                </Typography>
              </Box>
            )
          },
        },
        {
          field: 'issueDate',
          headerName: 'Issue Date',
          width: 120,
          type: 'date',
          valueGetter: (value: string | null) =>
            value ? dayjs(value).toDate() : undefined,
          valueFormatter: (value: Date | undefined) => formatIssueDate(value),
        },
        {
          field: 'expiration',
          headerName: 'Expiration',
          width: 120,
          type: 'date',
          valueGetter: (value: string | null) =>
            value ? dayjs(value).toDate() : undefined,
          valueFormatter: (value: Date | undefined) =>
            formatExpirationDate(value),
        },
        {
          field: 'status',
          headerName: 'Status',
          width: 130,
          sortComparator: (v1: string, v2: string) => {
            let priority: Record<string, number> = {
              'expiring-soon': 1,
              active: 2,
              expired: 3,
            }
            let p1 = priority[v1] ?? 4
            let p2 = priority[v2] ?? 4
            return p1 - p2
          },
          renderCell: (params) => {
            let color: 'success' | 'warning' | 'error' = (() => {
              switch (params.value) {
                case 'expired':
                  return 'error'
                case 'expiring-soon':
                  return 'warning'
                case 'active':
                  return 'success'
                default:
                  return 'success'
              }
            })()

            let icon = (() => {
              switch (params.value) {
                case 'expired':
                  return <ErrorIcon fontSize="small" />
                case 'expiring-soon':
                  return <WarningIcon fontSize="small" />
                case 'active':
                  return <CheckCircleIcon fontSize="small" />
                default:
                  return <CheckCircleIcon fontSize="small" />
              }
            })()

            return (
              <Chip
                label={params.value.replace('-', ' ')}
                icon={icon}
                color={color}
                size="medium"
                variant="outlined"
                sx={{ textTransform: 'capitalize' }}
                aria-label={`Status: ${params.value.replace('-', ' ')}`}
              />
            )
          },
        },
        {
          field: 'daysToExpiration',
          headerName: 'Days to Expiration',
          width: 150,
          renderCell: (params) => {
            if (params.value === undefined || params.value === null)
              return 'N/A'

            if (params.value < 0) {
              return `${Math.abs(params.value)} days expired`
            }

            return `${params.value} days remaining`
          },
        },
        {
          field: 'dateAdded',
          headerName: 'Date Added',
          width: 120,
          type: 'date',
          valueGetter: (value: string | null) =>
            value ? dayjs(value).toDate() : undefined,
          valueFormatter: (value: Date | undefined) => formatDateAdded(value),
        },
      ],
      [
        isCredentialDownloading,
        handleDownload,
        handleView,
        viewingId,
        verificationEnabled,
        formatIssueDate,
        formatExpirationDate,
        formatDateAdded,
      ],
    )

    // Custom credentials toolbar
    let CredentialsToolbar = useCallback(
      () => <DataGridToolbar fileName="user-credentials" />,
      [],
    )

    return (
      <Box>
        <Box className="px-6 mb-6">
          <Box className="flex items-center justify-between mb-2">
            <Typography
              variant="h6"
              className="flex items-center gap-2 mr-2 font-semibold"
              id="credentials-section-heading"
            >
              <LicenseIcon aria-hidden="true" />
              Credentials
            </Typography>
            <Box className="flex items-center gap-2">
              {totalCredentials > 0 && (
                <>
                  <Chip
                    label={`${totalCredentials} Total`}
                    size="medium"
                    variant="outlined"
                  />
                  {expiredCredentials > 0 && (
                    <Chip
                      label={`${expiredCredentials} Expired`}
                      size="medium"
                      variant="outlined"
                      color="error"
                    />
                  )}
                  {expiringSoonCredentials > 0 && (
                    <Chip
                      label={`${expiringSoonCredentials} Expiring Soon`}
                      size="medium"
                      variant="outlined"
                      color="warning"
                    />
                  )}
                </>
              )}
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={() => setIsCreateModalOpen(true)}
                aria-label="Create new credential"
              >
                Create Credential
              </Button>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            All credentials for this user. Use the toolbar to filter, search,
            and export to CSV format.
          </Typography>
        </Box>
        {selectedUserLicensesCredentialsStatus === 'rejected' ? (
          <Box
            className="grid py-12 place-items-center"
            role="alert"
            aria-labelledby="credentials-section-heading"
          >
            <Box className="text-center" sx={{ color: 'text.secondary' }}>
              <WarningIcon
                className="mb-4 opacity-50"
                sx={{
                  fontSize: 64,
                  color: 'warning.main',
                }}
                aria-hidden="true"
              />
              <Typography variant="h6" className="mb-2">
                Failed to Load Credentials
              </Typography>
              <Typography variant="body2">
                There was an error loading the credentials data. Please try
                refreshing the page.
              </Typography>
            </Box>
          </Box>
        ) : credentialsRows.length === 0 ? (
          <Box
            className="grid py-12 place-items-center"
            role="status"
            aria-labelledby="credentials-section-heading"
          >
            <Box className="text-center" sx={{ color: 'text.secondary' }}>
              <LicenseIcon
                className="mb-4 opacity-50"
                sx={{ fontSize: 64 }}
                aria-hidden="true"
              />
              <Typography variant="h6" className="mb-2">
                No Credentials Found
              </Typography>
              <Typography variant="body2">
                No credentials found for this user.
              </Typography>
            </Box>
          </Box>
        ) : selectedUserLicensesCredentialsStatus === 'pending' ? (
          <Skeleton
            variant="rectangular"
            height={500}
            sx={{ borderRadius: 1 }}
            aria-label="Loading credentials data"
          />
        ) : (
          <Box
            className="w-full h-full"
            sx={{ height: 500 }}
            role="region"
            aria-labelledby="credentials-section-heading"
          >
            <DataGridPremium
              rows={rowsWithVerification}
              columns={credentialsColumns}
              slots={{ toolbar: CredentialsToolbar }}
              showToolbar
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
                pinnedColumns: { right: ['actions'] },
              }}
              pageSizeOptions={[10, 25, 50]}
              checkboxSelection
              disableRowSelectionOnClick
              aria-label="User credentials"
              getRowId={(row) => (row as any).id}
              sx={{
                backgroundColor: 'background.paper',
                border: 1,
                borderRadius: 1,
                borderColor: 'divider',
                '& .MuiDataGrid-toolbarContainer': {
                  borderBottom: 1,
                  borderColor: 'divider',
                  p: 2,
                  backgroundColor: 'background.default',
                },
                '& .MuiDataGrid-cell': {
                  borderColor: 'divider',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: 'background.paperDark',
                  borderColor: 'divider',
                },
                '& .MuiDataGrid-footerContainer': {
                  borderColor: 'divider',
                  backgroundColor: 'background.paperDark',
                },
                '& .MuiDataGrid-row': {
                  '&:hover': {
                    backgroundColor: 'background.default',
                  },
                },
              }}
            />
          </Box>
        )}
        {/* Edit Credential Modal */}
        <EditCredentialModal
          isOpen={!!editModalCredential}
          credential={editModalCredential}
          userID={userID}
          onClose={() => setEditModalCredential(null)}
          onSuccess={() => handleSuccess('Credential updated successfully')}
          onFailure={handleFailure}
        />
        {/* Create Credential Modal */}
        <CreateCredentialModal
          isOpen={isCreateModalOpen}
          userID={userID}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={() => handleSuccess('Credential created successfully')}
          onFailure={handleFailure}
        />
        {/* Verify Credential Modal */}
        <VerifyCredentialModal
          isOpen={!!verifyModalCredential}
          credential={verifyModalCredential}
          userID={userID}
          onClose={() => setVerifyModalCredential(null)}
          onSuccess={() =>
            handleSuccess('Verification status updated successfully')
          }
          onFailure={handleFailure}
        />
        {/* View Credential Dialog */}
        <Dialog
          open={!!viewBlob}
          onClose={() => {
            setViewBlob(null)
            setViewedCredential(null)
          }}
          maxWidth="lg"
        >
          <DialogContent>
            {viewBlob && viewedCredential && (
              <img
                src={URL.createObjectURL(viewBlob)}
                alt={`${viewedCredential.type} credential`}
              />
            )}
          </DialogContent>
        </Dialog>
        {/* Snackbar for success/error messages */}
        <Snackbar
          open={snackbar.isOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, isOpen: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, isOpen: false })}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    )
  },
)

export default CredentialsDataGrid
